import React, { useMemo } from 'react';
import { Control } from 'react-hook-form-6';
import { UpdateContentItemFiltersFragment } from '../../../graphql/types';
import { Dropdown } from 'components/dropdown';

type ContentTag = NonNullable<
  UpdateContentItemFiltersFragment['contentTags']
>[number];
type ContentFilterGroup = NonNullable<
  UpdateContentItemFiltersFragment['contentFilterGroups']
>[number];

type FilterGroupsInputProps = {
  contentTags: ContentTag[];
  contentFilterGroups: ContentFilterGroup[];
  selectedTags: string[];
  control: Control;
};

export const FilterGroupsInput: React.FC<FilterGroupsInputProps> = ({
  contentTags,
  contentFilterGroups,
  selectedTags,
  control,
}) => {
  const [tagIdToTag, tagIdToFilterGroups, tagIdToFilterItemIds] =
    useMemo(() => {
      const tagIdToTag = new Map(contentTags.map((tag) => [tag.id, tag]));
      const tagIdToFilterGroups = new Map<string, ContentFilterGroup[]>();
      const tagIdToFilterItemIds = new Map<string, string[]>();

      for (const filterGroup of contentFilterGroups) {
        const tagId = filterGroup.contentTag?.id;
        if (!tagId) {
          continue;
        }

        const filterGroupsByTagId = tagIdToFilterGroups.get(tagId) ?? [];
        filterGroupsByTagId.push(filterGroup);
        tagIdToFilterGroups.set(tagId, filterGroupsByTagId);

        const filterItemIds = filterGroup.filterItems?.map((f) => f.id) ?? [];
        const filterItemIdsByTagId = tagIdToFilterItemIds.get(tagId) ?? [];
        tagIdToFilterItemIds.set(tagId, [
          ...filterItemIdsByTagId,
          ...filterItemIds,
        ]);
      }

      return [tagIdToTag, tagIdToFilterGroups, tagIdToFilterItemIds];
    }, [contentTags, contentFilterGroups]);

  const { tagsWithFiltersAvailable, tagsWithoutFiltersAvailable } =
    selectedTags.reduce<{
      tagsWithFiltersAvailable: ContentTag[];
      tagsWithoutFiltersAvailable: ContentTag[];
    }>(
      (acc, tagId) => {
        const tag = tagIdToTag.get(tagId);
        if (!tag) return acc;
        const filters = tagIdToFilterItemIds.get(tagId) ?? [];
        if (filters.length > 0) {
          acc.tagsWithFiltersAvailable.push(tag);
        } else {
          acc.tagsWithoutFiltersAvailable.push(tag);
        }
        return acc;
      },
      { tagsWithFiltersAvailable: [], tagsWithoutFiltersAvailable: [] },
    );

  return (
    <div>
      {tagsWithFiltersAvailable.length > 0 && (
        <div className="space-y-2">
          {tagsWithFiltersAvailable.map((tag) => {
            const tagId = tag.id;
            const fgs = tagIdToFilterGroups.get(tagId) ?? [];
            if (fgs.length > 0) {
              return (
                <div key={`${tagId}-Filters`}>
                  <Dropdown
                    name={`filters.${tagId}`}
                    label={`${tag.name} Filters`}
                    control={control}
                    isMulti
                    options={fgs.flatMap(
                      (fg) =>
                        fg.filterItems?.map((fi) => ({
                          label: `${fi.title} (${fg.title})`,
                          value: fi.id,
                        })) ?? [],
                    )}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
      {tagsWithoutFiltersAvailable.length > 0 && (
        <div>
          <h3 className="font-small">
            There are no filters available to select for the following tags:{' '}
            {tagsWithoutFiltersAvailable.map((tag) => tag.name).join(', ')}
          </h3>
        </div>
      )}
    </div>
  );
};
